import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useCreatePackagePreset,
  useDeletePackagePreset,
  useUpdatePackagePreset,
} from "@/api/packagePreset.api";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { PackagePresetDto } from "@/types/api/api-types";
import { PackagePresetFormData, PackagePresetSchema } from "@/types/forms/bo/packagePreset.form";

type Props = {
  open: boolean;
  onClose: () => void;
  preset: PackagePresetDto | null;
};

const toFormValues = (preset: PackagePresetDto | null): PackagePresetFormData => ({
  name: preset?.name ?? "",
  count: preset?.count ?? 1,
  weight: preset?.weight ?? (undefined as unknown as number),
  length: preset?.length ?? (undefined as unknown as number),
  width: preset?.width ?? (undefined as unknown as number),
  height: preset?.height ?? (undefined as unknown as number),
});

export const PackagePresetFormModal = ({ open, onClose, preset }: Props) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const { mutateAsync: createPreset, isPending: isCreating } = useCreatePackagePreset();
  const { mutateAsync: updatePreset, isPending: isUpdating } = useUpdatePackagePreset();
  const { mutateAsync: deletePreset, isPending: isDeleting } = useDeletePackagePreset();
  const isPending = isCreating || isUpdating || isDeleting;
  const isEdit = !!preset;

  const [confirmDelete, setConfirmDelete] = useState(false);

  const { control, handleSubmit, reset } = useForm<PackagePresetFormData>({
    resolver: zodResolver(PackagePresetSchema),
    defaultValues: toFormValues(preset),
  });

  useEffect(() => {
    if (!open) return;
    setConfirmDelete(false);
    reset(toFormValues(preset));
  }, [open, preset, reset]);

  const onSubmit = async (data: PackagePresetFormData) => {
    try {
      if (isEdit) await updatePreset({ id: preset.id, payload: data });
      else await createPreset(data);
      onClose();
    } catch {
      // Error toast is handled by the mutation
    }
  };

  const handleDelete = async () => {
    if (!preset) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    try {
      await deletePreset(preset.id);
      onClose();
    } catch {
      // Error toast is handled by the mutation
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t("packagePresetModal.editTitle") : t("packagePresetModal.createTitle")}
    >
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }}
        className="flex flex-col gap-4"
      >
        <ControlledUnifiedInput
          control={control}
          name="name"
          label={t("packagePresetModal.nameLabel")}
          required
          wrapperClassName="w-full"
        />
        <div className="grid grid-cols-2 gap-2">
          <ControlledUnifiedInput
            control={control}
            name="count"
            label={t("fedexPackageItem.countLabel")}
            inputType="integer"
            required
          />
          <ControlledUnifiedInput
            control={control}
            name="weight"
            label={t("fedexPackageItem.weightLabel")}
            inputType="float"
            required
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <ControlledUnifiedInput
            control={control}
            name="length"
            label={t("fedexPackageItem.lengthLabel")}
            inputType="integer"
            required
          />
          <ControlledUnifiedInput
            control={control}
            name="width"
            label={t("fedexPackageItem.widthLabel")}
            inputType="integer"
            required
          />
          <ControlledUnifiedInput
            control={control}
            name="height"
            label={t("fedexPackageItem.heightLabel")}
            inputType="integer"
            required
          />
        </div>
        <div className="flex flex-row flex-wrap items-center justify-between gap-2 pt-2">
          {isEdit ? (
            <Button
              type="button"
              variant={confirmDelete ? "destructive" : "ghost"}
              onClick={handleDelete}
              disabled={isPending}
              className={confirmDelete ? undefined : "text-red-600"}
            >
              <Trash2 className="size-4" />
              {confirmDelete
                ? t("packagePresetModal.confirmDelete")
                : t("packagePresetModal.delete")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex flex-row gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isCreating || isUpdating ? (
                <Loader2 className="animate-spin" />
              ) : isEdit ? (
                t("packagePresetModal.update")
              ) : (
                t("packagePresetModal.create")
              )}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
